import { useEffect, useRef, type RefObject } from 'react';

/**
 * Tracks text selection within a container element.
 * Fires a debounced callback when the user selects text (mouseup).
 * Used to detect if users manually select code instead of using the Copy button,
 * which may indicate a discoverability issue with the Copy button.
 */
export function useTextSelectionTracking(
  containerRef: RefObject<HTMLElement | null>,
  onSelect: (params: { selectionLength: number }) => void,
  debounceMs = 500
): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseUp = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        const selection = window.getSelection();
        const selectedText = selection?.toString() ?? '';
        if (selectedText.length > 0) {
          onSelect({ selectionLength: selectedText.length });
        }
      }, debounceMs);
    };

    container.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('mouseup', handleMouseUp);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [containerRef, onSelect, debounceMs]);
}
